import React, { useEffect } from 'react';
import { MessageSquare, Bot, AlertCircle } from 'lucide-react';
import { ChatHeader } from './ChatHeader';
import { MessageList } from './MessageList';
import { ChatInput } from './ChatInput';
import { CustomerDrawer } from './CustomerDrawer';
import { EmptyState } from '@/components/common/EmptyState';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useGetApiWorkspacesWorkspaceIdConversationsConversationId,
  useGetApiWorkspacesWorkspaceIdConversationsConversationIdCustomer,
  getGetApiWorkspacesWorkspaceIdConversationsQueryKey,
  postApiWorkspacesWorkspaceIdConversationsConversationIdRead,
} from '@/api/generated/conversations/conversations';
import { useQueryClient } from '@tanstack/react-query';
import {
  MOCK_CONVERSATION_ID,
  MOCK_CONVERSATION_DETAIL,
  MOCK_CUSTOMER_DTO,
} from '../mockData';
import type { ConversationDetail, CustomerDto, ConversationItem } from '@/types';
import { cn, getCustomerDisplayName } from '@/lib/utils';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';

interface ChatWindowProps {
  conversationId: string | null | undefined;
  className?: string;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({ conversationId, className }) => {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceStore();
  const isMock = conversationId === MOCK_CONVERSATION_ID;

  useEffect(() => {
    if (conversationId && !isMock && activeWorkspaceId) {
      postApiWorkspacesWorkspaceIdConversationsConversationIdRead(activeWorkspaceId, conversationId).catch(() => {});

      queryClient.setQueriesData<ConversationItem[]>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId) },
        (oldConversations) => {
          if (!Array.isArray(oldConversations)) return oldConversations;
          return oldConversations.map((conv) => {
            if (conv.id === conversationId && conv.unreadCount && conv.unreadCount > 0) {
              return {
                ...conv,
                unreadCount: 0,
              };
            }
            return conv;
          });
        }
      );
    }
  }, [conversationId, isMock, queryClient, activeWorkspaceId]);

  const {
    data: conversationData,
    isLoading: isQueryLoading,
    isError: isQueryError,
    refetch,
  } = useGetApiWorkspacesWorkspaceIdConversationsConversationId<ConversationDetail>(
    activeWorkspaceId!,
    conversationId || '',
    {
      query: {
        enabled: Boolean(conversationId) && !isMock && !!activeWorkspaceId,
      },
    }
  );

  const { data: customerDataQuery } = useGetApiWorkspacesWorkspaceIdConversationsConversationIdCustomer<CustomerDto>(
    activeWorkspaceId!,
    conversationId || '',
    {
      query: {
        enabled: Boolean(conversationId) && !isMock && !!activeWorkspaceId,
      },
    }
  );

  const isLoading = isMock ? false : isQueryLoading;
  const isError = isMock ? false : isQueryError;

  const conversation: ConversationDetail | null = isMock
    ? MOCK_CONVERSATION_DETAIL
    : conversationData && typeof conversationData === 'object' && 'id' in conversationData
    ? (conversationData as ConversationDetail)
    : null;

  const customerData = isMock ? MOCK_CUSTOMER_DTO : customerDataQuery;

  const rawCustomerName =
    customerData?.name ||
    (customerData as any)?.customerName ||
    conversation?.customerName;

  const rawCustomerEmail =
    customerData?.email ||
    (customerData as any)?.customerEmail ||
    conversation?.customerEmail;

  const activeCustomerDisplayName = getCustomerDisplayName(
    rawCustomerName,
    rawCustomerEmail
  );

  // If no conversation selected (e.g. desktop landing screen before picking a chat)
  if (!conversationId) {
    return (
      <div
        className={cn(
          'flex-1 flex flex-col items-center justify-center p-8 bg-card/40 text-center h-full select-none',
          className
        )}
      >
        <div className="h-16 w-16 rounded-3xl bg-primary/10 text-primary flex items-center justify-center mb-4 shadow-xs">
          <MessageSquare className="h-8 w-8" />
        </div>
        <h3 className="text-lg font-bold tracking-tight text-foreground">
          Central de Atendimento
        </h3>
        <p className="text-xs sm:text-sm text-muted-foreground max-w-sm mt-1.5 leading-relaxed">
          Selecione uma conversa ao lado para acompanhar as mensagens, ver o histórico ou assumir o atendimento.
        </p>
        <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-full border border-border/50">
          <Bot className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400" />
          <span>Atendimento conectado e pronto</span>
        </div>
      </div>
    );
  }

  // Loading conversation metadata
  if (isLoading) {
    return (
      <div className={cn('flex-1 flex flex-col h-full bg-background', className)}>
        {/* Header Skeleton */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="space-y-1.5">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
          </div>
          <Skeleton className="h-8 w-28 rounded-md" />
        </div>

        {/* Message stream skeleton */}
        <div className="flex-1 p-6 space-y-4">
          <Skeleton className="h-12 w-1/2 rounded-2xl" />
          <Skeleton className="h-14 w-2/3 ml-auto rounded-2xl" />
          <Skeleton className="h-12 w-1/3 rounded-2xl" />
        </div>

        {/* Input skeleton */}
        <div className="p-4 border-t border-border">
          <Skeleton className="h-11 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !conversation) {
    return (
      <div className={cn('flex-1 flex items-center justify-center p-8 bg-card/40 h-full', className)}>
        <EmptyState
          icon={AlertCircle}
          title="Conversa não encontrada"
          description="Não foi possível carregar os detalhes da conversa ou o ticket de chat foi arquivado."
          actionLabel="Tentar Novamente"
          onAction={() => refetch()}
        />
      </div>
    );
  }

  const statusStr = String(conversation.status).toUpperCase();
  const isClosed = statusStr === 'CLOSED' || statusStr === 'RESOLVED' || conversation.status === 3;

  return (
    <div
      id="tour-chat-window"
      className={cn('flex-1 flex flex-col h-full min-w-0 bg-background relative', className)}
    >
      {/* Top Header */}
      <ChatHeader conversation={conversation} />

      {/* Message Stream */}
      <MessageList
        conversationId={conversation.id}
        customerName={activeCustomerDisplayName}
        isClosed={isClosed}
        closedAtUtc={conversation.closedAtUtc}
      />

      {/* Input Composer */}
      <ChatInput
        conversationId={conversation.id}
        status={conversation.status}
        closedAtUtc={conversation.closedAtUtc}
      />

      {/* Slide-out Customer Profile Drawer */}
      <CustomerDrawer conversation={conversation} />
    </div>
  );
};

export default ChatWindow;
